import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  GraduationCap,
  LogOut,
  Menu,
  ShieldCheck,
  UserCheck,
  Users,
  X,
  FileText,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { userService } from "../../../services/userService";
import {
  getActiveEnrollmentContext,
  getEligibleStudents,
  getSemesterEnrollments,
} from "../../../services/enrollmentService";
import { getInstitutionInitials } from "../../../utils/institutionBranding";
import EnrollmentManagement from "../../academic-management/components/EnrollmentManagement";
import UnitManagement from "../../academic-management/components/UnitManagement";
import AcademicManagement from "../../academic-management";
import TimetableManagement from "../../timetable-management";
import MyTimetable from "./MyTimetable";
import SummativeAssessmentPortal from "../../summative-assessment-portal";
import SummativeAssessmentScheduleManagement from "../../summative-assessment-schedule-management";
import SummativeResultApprovals from "../../summative-assessment-portal/components/SummativeResultApprovals";
import AdministrativeWorkflowCenter from "../../administrative-workflow-center";
import { StudentList, StudentDetail } from "../../students";
import AdmissionsManagement from "../../admissions";
import DepartmentTrainerManagement from "./DepartmentTrainerManagement";
import FormativeAssessmentManagement from "../../formative-assessment-management";
import POEManagementHub from "../../poe-management-hub";
import FinanceDashboard from "../../finance";
import TransportWorkspace from "../../transport/TransportWorkspace";
import ResourceServiceBooking from "../../resource-service-booking";
import HostelReservationPortal from "../../hostel/HostelReservationPortal";
const enrollmentRoles = [
  "system_administrator",
  "chief_principal",
  "deputy_principal_academic",
  "registrar_academic",
  "dean_of_faculty",
  "head_of_department",
  "head_of_section",
];
const centralAdmissionsRoles = [
  "system_administrator",
  "chief_principal",
  "deputy_principal_academic",
  "registrar_academic",
];
const timetableLeadershipRoles = [
  "system_administrator",
  "chief_principal",
  "deputy_principal_academic",
  "registrar_academic",
  "dean_of_faculty",
  "head_of_department",
  "head_of_section",
  "timetable_master",
];
const summativeTimetableRoles = [
  ...timetableLeadershipRoles,
  "assessment_officer",
];
const trainerManagementRoles = [
  "system_administrator",
  "chief_principal",
  "deputy_principal_academic",
  "registrar_academic",
  "dean_of_faculty",
  "head_of_department",
  "head_of_section",
];
const summativeMarksRoles = [
  "system_administrator",
  "chief_principal",
  "deputy_principal_academic",
  "registrar_academic",
  "dean_of_faculty",
  "head_of_department",
  "head_of_section",
  "assessment_officer",
  "trainer_lecturer",
  "trainer",
  "teacher",
  "lecturer",
];
const trainerOnlyDashboardRoles = [
  "trainer_lecturer",
  "trainer",
  "teacher",
  "lecturer",
];
const summativeBookingRoles = [
  "system_administrator",
  "chief_principal",
  "deputy_principal_academic",
  "registrar_academic",
  "dean_of_faculty",
  "head_of_department",
  "head_of_section",
  "assessment_officer",
];
const summativeApprovalRoles = [
  "system_administrator",
  "chief_principal",
  "deputy_principal_academic",
  "registrar_academic",
  "dean_of_faculty",
  "head_of_department",
  "head_of_section",
  "assessment_officer",
  "quality_assurance_officer",
  "senate",
  "senate_member",
  "senate_secretary",
];
const academicOperationsRoles = [
  "system_administrator",
  "chief_principal",
  "deputy_principal_academic",
  "deputy_principal_administration",
  "registrar_academic",
  "dean_of_faculty",
  "head_of_department",
  "head_of_section",
];
const normalizeStaffRole = (role) => {
  const normalized = String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return normalized === "hod" || normalized === "head_department"
    ? "head_of_department"
    : normalized;
};
const readSecondaryRoles = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  const normalizedValue = value.trim();
  try {
    const parsed = JSON.parse(normalizedValue);
    return Array.isArray(parsed)
      ? parsed
      : normalizedValue.replace(/^\{|\}$/g, "").split(",");
  } catch (_) {
    return normalizedValue.replace(/^\{|\}$/g, "").split(",");
  }
};
const menus = [
  {
    label: "Academic Operations",
    items: [
      ["Student enrollment", "enrollment"],
      ["Programmes & units", "programs"],
      ["Trainer management", "trainer_management"],
      ["Department timetables", "timetable_management"],
      ["Summative examinations", "summative_bookings"],
      ["Summative timetable", "summative_timetable"],
      ["Formative Assessments", "formative_assessments"],
    ],
  },
  {
    label: "Teaching",
    items: [
      ["My units", "units"],
      ["My timetable", "timetable"],
    ],
  },
  {
    label: "Assessment & POE",
    items: [
      ["Formative entry", "formative_entry"],
      ["Summative entry", "assessments"],
      ["POE management", "poe_management"],
    ],
  },
  {
    label: "Approvals",
    items: [
      ["Workflow approvals", "approvals"],
      ["Summative result approvals", "summative_result_approvals"],
    ],
  },
  {
    label: "Students",
    items: [
      ["Student records", "students"],
      ["Admissions", "admissions"],
    ],
  },
  { label: "Finance", items: [["Fee configuration", "finance"]] },
  {
    label: "Services",
    items: [
      ["Book a space", "resource_booking"],
      ["Book hostel", "hostel_booking"],
      ["Transport request", "transport_request"],
    ],
  },
];

const StaffDashboard = ({ initialWorkspace = null }) => {
  const { user, profile, signOut } = useAuth();
  const { institutionSettings: institution } = useTheme();
  const navigate = useNavigate();
  const [staffProfile, setStaffProfile] = React.useState(null);
  const [mobile, setMobile] = React.useState(false);
  const [stats, setStats] = React.useState({
    eligible: 0,
    reported: 0,
    active: 0,
    completed: 0,
  });
  const [transportNotice, setTransportNotice] = React.useState(null);
  const [workspace, setWorkspace] = React.useState(() => {
    if (initialWorkspace) return initialWorkspace;
    const prefix = "/role-based-dashboard/";
    const path = window.location.pathname;
    return path.startsWith(prefix)
      ? path.slice(prefix.length).replace(/\/+$/, "") || "dashboard"
      : "dashboard";
  });
  const [selectedStudentId, setSelectedStudentId] = React.useState(null);
  const assignedRoles = [
    profile?.primary_role,
    ...readSecondaryRoles(profile?.secondary_roles),
  ]
    .filter(Boolean)
    .map(normalizeStaffRole);
  const isHeadRole = assignedRoles.some((role) =>
    ["head_of_department", "head_of_section"].includes(role),
  );
  const isAcademicStaff = userService.isAcademicStaffMember({
    ...profile,
    department: staffProfile?.department || profile?.department,
    staff: staffProfile?.staff,
  });
  const hasAcademicScope = !isHeadRole || isAcademicStaff;
  const canManageEnrollment = hasAcademicScope && assignedRoles.some((role) =>
    enrollmentRoles.includes(role),
  );
  const canViewAdmissions = hasAcademicScope && assignedRoles.some((role) =>
    enrollmentRoles.includes(role),
  );
  const canAdministerAdmissions = hasAcademicScope && assignedRoles.some((role) =>
    centralAdmissionsRoles.includes(role),
  );
  const canManageTimetables = hasAcademicScope && assignedRoles.some((role) =>
    timetableLeadershipRoles.includes(role),
  );
  const canManageSummativeTimetable = hasAcademicScope && assignedRoles.some((role) =>
    summativeTimetableRoles.includes(role),
  );
  const canManageTrainers = hasAcademicScope && assignedRoles.some((role) =>
    trainerManagementRoles.includes(role),
  );
  const canEnterSummativeMarks = hasAcademicScope && assignedRoles.some((role) =>
    summativeMarksRoles.includes(role),
  );
  const canViewSummativeBookings = hasAcademicScope && assignedRoles.some((role) =>
    summativeBookingRoles.includes(role),
  );
  const canReviewSummativeResults = hasAcademicScope && assignedRoles.some((role) =>
    summativeApprovalRoles.includes(role),
  );
  const canViewAcademicOperations = hasAcademicScope && assignedRoles.some((role) =>
    academicOperationsRoles.includes(role),
  );
  const isNonAcademicHead = isHeadRole && !isAcademicStaff;
  const isTrainerOnlyDashboard = trainerOnlyDashboardRoles.includes(
    normalizeStaffRole(profile?.primary_role),
  );
  const roleMenus = isTrainerOnlyDashboard
    ? menus.flatMap((menu) =>
        menu.label === "Assessment & POE"
          ? [
              {
                label: "Assessment",
                items: [
                  ["Formative entry", "formative_entry"],
                  ["Summative entry", "assessments"],
                ],
              },
              {
                label: "POE Management",
                items: [["POE management", "poe_management"]],
              },
            ]
          : [menu],
      )
    : menus;
  const visibleMenus = roleMenus.filter(
    (menu) => {
      // A non-academic HOD (for example, Estates) keeps the shared staff
      // shell and service requests, but must not see teaching, assessment,
      // approvals, student-management or finance menus.
      if (isNonAcademicHead) return menu.label === "Services";
      if (menu.label === "Academic Operations") return canViewAcademicOperations;
      if (menu.label === "Students") return canManageEnrollment;
      if (menu.label === "Finance") return canManageEnrollment;
      return true;
    },
  );
  const location = useLocation();

  React.useEffect(() => {
    const prefix = "/role-based-dashboard/";
    if (location.pathname.startsWith(prefix)) {
      const target =
        location.pathname.slice(prefix.length).replace(/\/+$/, "") ||
        "dashboard";
      if (target && target !== workspace) setWorkspace(target);
    }
    if (location.pathname === "/finance" && workspace !== "finance") {
      setWorkspace("finance");
    } else if (location.pathname === "/" && workspace === "finance") {
      setWorkspace("dashboard");
    }
  }, [location.pathname]);

  const go = (target) => {
    setMobile(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Staff navigation is workspace-based. Keeping targets in this shell
    // preserves the staff header, dropdowns and role scope for every module.
    setWorkspace(target || "dashboard");
    navigate(
      target && target !== "dashboard"
        ? `/role-based-dashboard/${target}`
        : "/role-based-dashboard",
      { replace: true },
    );
  };

  const notifyTransport = React.useCallback((message, type = "success") => {
    setTransportNotice({ message, type });
    window.setTimeout(() => setTransportNotice(null), 3500);
  }, []);

  React.useEffect(() => {
    userService.getCurrentUserProfile(user?.id).then(setStaffProfile);
  }, [user?.id]);
  React.useEffect(() => {
    if (!canManageEnrollment) return;
    Promise.all([getEligibleStudents(), getActiveEnrollmentContext()])
      .then(async ([students, contextResult]) => {
        const context = contextResult.success ? contextResult.data : null;
        let enrollments = [];
        if (context?.academicYear && context?.semester)
          enrollments = await getSemesterEnrollments(
            context.academicYear.id,
            context.semester.id,
          );
        setStats({
          eligible: students.length,
          reported: enrollments.filter((row) => row.status === "reported")
            .length,
          active: enrollments.filter((row) => row.status === "active").length,
          completed: enrollments.filter((row) =>
            ["registration_completed", "billed"].includes(row.status),
          ).length,
        });
      })
      .catch((error) =>
        console.warn("Unable to load staff enrollment summary", error),
      );
  }, [canManageEnrollment]);

  const staff =
    (Array.isArray(staffProfile?.staff)
      ? staffProfile.staff[0]
      : staffProfile?.staff) || {};
  const fullName =
    staffProfile?.full_name || user?.user_metadata?.full_name || "Staff member";
  const institutionInitials = getInstitutionInitials(institution);
  const formatRole = (value) =>
    String(value || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  const responsibilities = (
    profile?.secondary_roles ||
    staffProfile?.secondary_roles ||
    []
  )
    .map(formatRole)
    .join(", ");
  const details = [
    ["PF number", staff.employee_id],
    [
      "Official email",
      staff.institutional_email || staffProfile?.email || user?.email,
    ],
    [
      "Job title",
      formatRole(
        profile?.primary_role || staffProfile?.primary_role || "staff",
      ),
    ],
    [
      "Role / responsibilities",
      responsibilities || "No additional responsibilities",
    ],
    ["Department", staff.department?.name || staffProfile?.department?.name],
    ["Personal email", staffProfile?.personal_email],
  ];

  const Nav = ({ stacked = false }) => (
    <>
      {visibleMenus.map((menu) => (
        <div
          key={menu.label}
          className={
            stacked
              ? "border-b py-2"
              : "group relative z-10 flex h-16 items-center transition-[z-index] hover:z-[120]"
          }
        >
          <button
            className={`flex items-center gap-1 font-medium ${stacked ? "w-full justify-between py-2" : "hover:text-primary-foreground/80"}`}
          >
            {menu.label}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <div
            className={
              stacked
                ? "pb-2"
                : "invisible absolute left-0 top-14 z-[110] w-60 rounded-lg border bg-white p-2 text-slate-800 opacity-0 shadow-xl transition group-hover:visible group-hover:opacity-100"
            }
          >
            {menu.items
              .filter(
                ([, target]) =>
                  (target !== "timetable_management" || canManageTimetables) &&
                  (target !== "summative_timetable" ||
                    canManageSummativeTimetable) &&
                  (target !== "trainer_management" || canManageTrainers) &&
                  (target !== "finance" || canManageEnrollment) &&
                  (target !== "assessments" || canEnterSummativeMarks) &&
                  (target !== "summative_bookings" ||
                    canViewSummativeBookings) &&
                  (target !== "summative_result_approvals" ||
                    canReviewSummativeResults),
              )
              .map(([label, target]) => {
                const blocked = target === "enrollment" && !canManageEnrollment;
                return (
                  <button
                    key={target}
                    disabled={blocked}
                    onClick={() => go(target)}
                    className="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm hover:bg-secondary disabled:text-slate-400"
                  >
                    <span>{label}</span>
                    {blocked && (
                      <span className="text-[0.625rem] uppercase">
                        Restricted
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-[100] bg-primary text-primary-foreground shadow-lg">
        <div className="mx-auto flex min-h-16 max-w-[1600px] items-center gap-5 px-5">
          <button
            onClick={() => go("dashboard")}
            aria-label={`${institutionInitials} portal home`}
            className="flex shrink-0 items-center gap-3"
          >
            {institution?.logo_url ? (
              <img
                src={institution.logo_url}
                alt="Logo"
                className="h-10 w-10 rounded bg-white object-contain p-1"
              />
            ) : (
              <span className="grid h-10 w-10 place-items-center rounded bg-primary-foreground/20">
                <GraduationCap />
              </span>
            )}
            <span className="text-sm font-bold tracking-[0.18em]">
              {institutionInitials}
            </span>
          </button>
          <nav className="hidden flex-1 items-center justify-center gap-7 lg:flex">
            <Nav />
          </nav>
          <button
            className="ml-auto lg:hidden"
            onClick={() => setMobile((v) => !v)}
          >
            {mobile ? <X /> : <Menu />}
          </button>
          <div className="hidden text-right lg:block">
            <p className="max-w-52 truncate text-sm font-semibold">
              {fullName}
            </p>
            <button
              onClick={async () => {
                await signOut();
                navigate("/login");
              }}
              className="text-xs text-primary-foreground/65 hover:text-primary-foreground"
            >
              Sign out
            </button>
          </div>
        </div>
        {mobile && (
          <nav className="border-t bg-white px-5 text-slate-800 lg:hidden">
            <Nav stacked />
            <button
              onClick={async () => {
                await signOut();
                navigate("/login");
              }}
              className="flex gap-2 py-4 text-red-700"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-[1600px] space-y-4 px-5 py-5">
        {workspace !== "dashboard" ? (
          <section className="space-y-5">
            {![
              "programs",
              "units",
              "formative_entry",
              "timetable",
              "timetable_management",
              "enrollment",
              "trainer_management",
              "formative_assessments",
              "students",
              "admissions",
              "poe_management",
              "approvals",
              "transport_request",
              "resource_booking",
            ].includes(workspace) && (
              <button
                onClick={() => go("dashboard")}
                className="text-sm font-semibold text-blue-700 hover:underline"
              >
                ← Staff dashboard
              </button>
            )}
            {workspace === "enrollment" && canManageEnrollment && (
              <EnrollmentManagement
                onBack={() => go("dashboard")}
                roleBadge={formatRole(profile?.primary_role || "staff")}
              />
            )}
            {workspace === "programs" && (
              <AcademicManagement
                initialTab="programs"
                embedded
                onBack={() => go("dashboard")}
              />
            )}
            {["units", "formative_entry"].includes(workspace) && (
              <UnitManagement
                trainerOnly
                formativeOnly={workspace === "formative_entry"}
                hideHeader
                titleBar={
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => go("dashboard")}
                      className="text-xs font-semibold text-blue-700 hover:underline"
                    >
                      ← Staff dashboard
                    </button>
                    <span className="text-slate-300 text-xs">/</span>
                    <h1 className="text-sm font-bold text-slate-900">
                      {workspace === "formative_entry" ? "Formative entry" : "My Units"}
                    </h1>
                  </div>
                }
              />
            )}
            {workspace === "timetable" && (
              <MyTimetable onBack={() => go("dashboard")} />
            )}
            {workspace === "timetable_management" && canManageTimetables && (
              <TimetableManagement
                embedded
                onBack={() => go("dashboard")}
                roleBadge={formatRole(profile?.primary_role || "staff")}
              />
            )}
            {workspace === "summative_timetable" &&
              canManageSummativeTimetable && (
                <SummativeAssessmentScheduleManagement embedded />
              )}
            {workspace === "trainer_management" && canManageTrainers && (
              <DepartmentTrainerManagement
                homeDepartmentId={
                  staff.department_id || staffProfile?.department?.id
                }
                homeDepartmentName={
                  staff.department?.name || staffProfile?.department?.name
                }
                onOpenUnits={() => go("programs")}
                onOpenApprovals={() => go("approvals")}
                onBack={() => go("dashboard")}
                roleBadge={formatRole(profile?.primary_role || "staff")}
              />
            )}
            {["assessments", "summative_bookings"].includes(workspace) && (
              <SummativeAssessmentPortal embedded />
            )}
            {workspace === "formative_assessments" && (
              <FormativeAssessmentManagement />
            )}
            {workspace === "poe_management" && (
              <POEManagementHub embedded onBack={() => go("dashboard")} />
            )}
            {workspace === "approvals" && (
              <AdministrativeWorkflowCenter
                embedded
                onBack={() => go("dashboard")}
              />
            )}
            {workspace === "summative_result_approvals" &&
              canReviewSummativeResults && <SummativeResultApprovals />}
            {workspace === "summative_result_approvals" &&
              !canReviewSummativeResults && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
                  Summative result approval access is restricted to authorised
                  academic leadership.
                </div>
              )}
            {workspace === "students" && !selectedStudentId && (
              <StudentList
                embedded
                onViewStudent={(id) => setSelectedStudentId(id)}
              />
            )}
            {workspace === "students" && selectedStudentId && (
              <StudentDetail
                embedded
                studentId={selectedStudentId}
                onBack={() => setSelectedStudentId(null)}
              />
            )}
            {workspace === "admissions" && canViewAdmissions && (
              <AdmissionsManagement
                embedded
                readOnly={!canAdministerAdmissions}
              />
            )}
            {workspace === "admissions" && !canViewAdmissions && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
                Admissions access is restricted to authorised academic
                leadership.
              </div>
            )}
            {workspace === "enrollment" && !canManageEnrollment && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
                Student enrollment management is not assigned to your role.
              </div>
            )}
            {workspace === "finance" && canManageEnrollment && (
              <FinanceDashboard
                embedded
                onBack={() => go("dashboard")}
                roleBadge={formatRole(profile?.primary_role || "staff")}
              />
            )}
            {workspace === "transport_request" && (
              <TransportWorkspace
                notify={notifyTransport}
                view="requests"
                manager={false}
                onBack={() => go("dashboard")}
              />
            )}
            {workspace === "resource_booking" && (
              <ResourceServiceBooking
                embedded
                onBack={() => go("dashboard")}
              />
            )}
            {workspace === "hostel_booking" && (
              <HostelReservationPortal embedded />
            )}
          </section>
        ) : (
          <>
            <section className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">
                  Staff dashboard
                </p>
                <h1 className="mt-1 text-2xl font-bold">Welcome, {fullName}</h1>
                <p className="mt-1 text-sm capitalize text-slate-500">
                  {String(profile?.primary_role || "staff").replaceAll(
                    "_",
                    " ",
                  )}
                  {staff.department?.name || staffProfile?.department?.name
                    ? ` · ${staff.department?.name || staffProfile?.department?.name}`
                    : ""}
                </p>
              </div>
              {canManageEnrollment && (
                <button
                  onClick={() => go("enrollment")}
                  className="flex items-center gap-2 rounded-md bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  <UserCheck className="h-4 w-4" />
                  Manage enrollment
                </button>
              )}
            </section>

            {canManageEnrollment ? (
              <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {[
                  [
                    "Eligible students",
                    stats.eligible,
                    Users,
                    "bg-blue-100 text-blue-700",
                  ],
                  [
                    "Awaiting activation",
                    stats.reported,
                    ClipboardCheck,
                    "bg-amber-100 text-amber-700",
                  ],
                  [
                    "Active registrations",
                    stats.active,
                    ShieldCheck,
                    "bg-emerald-100 text-emerald-700",
                  ],
                  [
                    "Completed",
                    stats.completed,
                    CheckCircle2,
                    "bg-indigo-100 text-indigo-700",
                  ],
                ].map(([label, count, Icon, tone]) => (
                  <button
                    key={label}
                    onClick={() => go("enrollment")}
                    className="flex items-center gap-3 rounded-lg border bg-white p-4 text-left hover:border-blue-300"
                  >
                    <span
                      className={`grid h-9 w-9 place-items-center rounded-lg ${tone}`}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span>
                      <strong className="block text-xl leading-none">
                        {count}
                      </strong>
                      <small className="text-xs text-slate-500">{label}</small>
                    </span>
                  </button>
                ))}
              </section>
            ) : (
              <section className="flex items-center gap-3 rounded-lg border bg-white p-4 text-sm text-slate-600">
                <BookOpen className="h-5 w-5 shrink-0 text-blue-700" />
                <div>
                  <strong className="block text-slate-900">
                    Teaching workspace
                  </strong>
                  <span>
                    Your assigned units, timetable and assessments are
                    available from the Teaching menu.
                  </span>
                </div>
              </section>
            )}

            <section className="grid gap-4 lg:grid-cols-[1fr_300px]">
              <div className="rounded-lg border bg-white p-4">
                <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
                  <div>
                    <h2 className="text-lg font-bold">Quick operations</h2>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Role-based access to your daily tasks.
                    </p>
                  </div>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {[
                    ["Student enrollment", "enrollment", UserCheck],
                    ["Programmes and units", "programs", BookOpen],
                    ["Trainer management", "trainer_management", Users],
                    ["Workflow approvals", "approvals", ClipboardCheck],
                    [
                      "Summative result approvals",
                      "summative_result_approvals",
                      ClipboardCheck,
                    ],
                    ["Student records", "students", Users],
                    ["Book a space", "resource_booking", CalendarDays],
                    ["My timetable", "timetable", CalendarDays],
                    ["Summative entry", "assessments", CheckCircle2],
                    [
                      "Summative examinations",
                      "summative_bookings",
                      CheckCircle2,
                    ],
                    [
                      "Summative timetable",
                      "summative_timetable",
                      CalendarDays,
                    ],
                    [
                      "Formative entry",
                      "formative_entry",
                      ClipboardCheck,
                    ],
                    ["POE management", "poe_management", FileText],
                  ]
                    .filter(
                      ([, target]) =>
                        (target !== "enrollment" || canManageEnrollment) &&
                        (target !== "students" || canManageEnrollment) &&
                        (target !== "trainer_management" ||
                          canManageTrainers) &&
                        (target !== "summative_timetable" ||
                          canManageSummativeTimetable) &&
                        (target !== "assessments" || canEnterSummativeMarks) &&
                        (target !== "summative_bookings" ||
                          canViewSummativeBookings) &&
                        (target !== "summative_result_approvals" ||
                          canReviewSummativeResults),
                    )
                    .map(([label, target, Icon]) => (
                      <button
                        key={target}
                        onClick={() => go(target)}
                        className="flex items-center gap-2.5 rounded-md border px-3 py-3 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Icon className="h-4 w-4 shrink-0 text-blue-700" />
                        <span className="font-medium text-slate-800">
                          {label}
                        </span>
                      </button>
                    ))}
                </div>
              </div>
              <aside className="rounded-lg border bg-white p-4">
                <h2 className="text-base font-bold">Profile</h2>
                <dl className="mt-3 space-y-2">
                  {details.map(([label, val]) => (
                    <div
                      key={label}
                      className="border-b border-slate-100 pb-2 last:border-0 last:pb-0"
                    >
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        {label}
                      </dt>
                      <dd className="mt-0.5 break-words text-sm font-medium capitalize">
                        {val || "Not provided"}
                      </dd>
                    </div>
                  ))}
                </dl>
              </aside>
            </section>
          </>
        )}
      </main>
      {transportNotice && (
        <div
          className={`fixed bottom-5 right-5 z-[160] max-w-sm rounded-lg border px-4 py-3 text-sm font-semibold shadow-lg ${
            transportNotice.type === "error"
              ? "border-rose-200 bg-rose-50 text-rose-800"
              : "border-emerald-200 bg-emerald-50 text-emerald-800"
          }`}
          role="status"
        >
          {transportNotice.message}
        </div>
      )}
    </div>
  );
};
export default StaffDashboard;
